import React from "react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "../components/ui/card";
import PageWithNavbar from "@/components/PageWithNavbar";
import DownloadCard, { ProductConfig } from "@/components/DownloadCard";

// Configuration for all downloadable products
const PRODUCTS: ProductConfig[] = [
  {
    name: "Doyen Manager",
    description: "Download the latest Doyen Manager installer for your platform.",
    manifestPath: "/Downloads/update.json",
    signature: "1130 Lab LLC",
    countersignature: "Sectigo Public Time Stamping Signer R36",
  },
  {
    name: "Doyen Core",
    description: "Download the latest Doyen Core release directly.",
    manifestPath: "/Downloads/core/update.json",
    signature: "1130 Lab LLC",
    countersignature: "Sectigo Public Time Stamping Signer R36",
  }
  // Add more products here as needed:
  // {
  //   name: "Doyen CLI",
  //   description: "Command-line interface for Doyen trading platform.",
  //   manifestPath: "/Downloads/cli/update.json",
  //   signature: "1130 Lab LLC",
  //   countersignature: "Sectigo Public Time Stamping Signer R36",
  // },
];

export const DownloadsPage: React.FC = () => {
  async function detectPlatformAndArch(): Promise<{ os: string; arch: string }> {
    const ua = window.navigator.userAgent || "";
    let os: string = "unknown";
    if (/Win/i.test(ua)) os = "windows";
    else if (/Mac/i.test(ua)) os = "mac";
    else if (/Linux/i.test(ua)) os = "linux";

    let arch: string = "unknown";

    // Prefer User-Agent Client Hints (Chromium-based browsers)
    const uaData = (navigator as any).userAgentData;
    if (uaData?.getHighEntropyValues) {
      try {
        const hints = await uaData.getHighEntropyValues(["architecture", "bitness"]);
        const architecture = hints.architecture || "";
        const bitness = hints.bitness || "";
        if (/arm/i.test(architecture)) {
          arch = bitness === "32" ? "arm" : "arm64";
        } else if (/x86|x64|amd64/i.test(architecture)) {
          arch = bitness === "32" ? "x86" : "x64";
        } else if (bitness) {
          arch = bitness === "32" ? "x86" : "x64";
        }
      } catch {
        // ignore client hints errors and fallback
      }
    }

    // Fallback: parse userAgent tokens
    if (arch === "unknown") {
      if (/\b(aarch64|arm64|armv8|arm64e)\b/i.test(ua)) arch = "arm64";
      else if (/\b(arm|armv7|armv6)\b/i.test(ua)) arch = "arm";
      else if (/\b(x86_64|x86-64|amd64|x64)\b/i.test(ua)) arch = "x64";
      else if (/\b(i386|i686|win32|wow64|x86)\b/i.test(ua)) arch = "x86";
    }

    // Last fallback: navigator.platform hints
    if (arch === "unknown" && navigator.platform) {
      const plt = navigator.platform;
      if (/\b(arm|aarch64|arm64)\b/i.test(plt)) arch = "arm64";
      else if (/\b(Win32|i386|i686|x86)\b/i.test(plt)) arch = "x86";
      else if (/\b(x86_64|x64|amd64|MacIntel)\b/i.test(plt)) arch = "x64";
    }

    return { os, arch };
  }

  const [detected, setDetected] = React.useState<{ os: string; arch: string }>({ os: "unknown", arch: "unknown" });

  React.useEffect(() => {
    let cancelled = false;
    detectPlatformAndArch().then((result) => {
      if (!cancelled) setDetected(result);
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <PageWithNavbar>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="mb-6">
            <h1 className="text-3xl md:text-4xl font-bold">
              Download{" "}
              <span className="bg-gradient-to-b from-primary/60 to-primary text-transparent bg-clip-text">
                Doyen
              </span>          
            </h1>
          </div>
          
          {/* Render all downloadable products */}
          <div className="space-y-6">
            {PRODUCTS.map((product, index) => (
              <DownloadCard key={index} product={product} detected={detected} />
            ))}
          </div>

          {/* Additional download sections */}
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Backtesting Data</CardTitle>
              <CardDescription>
                Below are links to historical tick-level book and trade data for backtesting.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="mb-4">
                Coming soon. We aim to provide a month of clean tick-level data as a freely available download.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </PageWithNavbar>
  );
};

export default DownloadsPage;